import React, { useEffect } from "react";
import ReactDOM from "react-dom/client";
import App from "./App.jsx";
import "./index.css";
import { ClerkProvider, useAuth } from "@clerk/clerk-react";
import { shadesOfPurple } from "@clerk/themes";
import { initializePixel, trackRegistration } from './utils/pixelService.js';
import { setGaUserId, trackSignUp, trackLogin } from './utils/gaService.js';
import { identifyHotjarUser } from './utils/hotjarService.js';
import { esMX } from '@clerk/localizations';

// Initialize Facebook Pixel
initializePixel();

// Disable console in production
if (import.meta.env.REACT_APP_NODE_ENV === 'production') {
  if (typeof window !== "undefined") {
    window.console.log = () => { };
    window.console.warn = () => { };
    // Uncomment the next line if you want to disable error logs as well
    // window.console.error = () => {};
  }
}

// Component to handle auth state changes and tracking
const AuthObserver = ({ children }) => {
  const { isLoaded, userId, isSignedIn } = useAuth();
  
  useEffect(() => {
    // Solo ejecutar si la autenticación está cargada y el usuario está autenticado
    if (!isLoaded || !isSignedIn || !userId) return;
    
    // Establecer ID de usuario en Google Analytics para mejor seguimiento
    setGaUserId(userId);
    
    // Identificar usuario en Hotjar con su ID único
    identifyHotjarUser(userId, {
      authenticated: true,
      createdAt: localStorage.getItem(`clerk_signup_time_${userId}`) || 'unknown'
    });
    
    // Verificar si es un nuevo registro (usando localStorage para evitar eventos duplicados)
    const hasTrackedRegistration = localStorage.getItem(`tracked_registration_${userId}`);
    const hasTrackedLogin = sessionStorage.getItem(`tracked_login_${userId}`);
    
    if (!hasTrackedRegistration) {
      // Solo rastrear para usuarios nuevos (los que se registraron en las últimas 2 horas)
      const signUpTime = localStorage.getItem(`clerk_signup_time_${userId}`);
      const isNewUser = signUpTime && (Date.now() - parseInt(signUpTime, 10) < 7200000); // 2 horas
      
      if (isNewUser) {
        // Rastrear evento de registro tanto en Facebook Pixel como en Google Analytics
        trackRegistration('website', { user_id: userId });
        trackSignUp('website', { user_id: userId });
        
        // Marcar como rastreado
        localStorage.setItem(`tracked_registration_${userId}`, 'true');
      }
    }
    
    // Rastrear inicio de sesión (solo una vez por sesión)
    if (!hasTrackedLogin) {
      trackLogin('website', { user_id: userId });
      sessionStorage.setItem(`tracked_login_${userId}`, 'true');
    }
  }, [isLoaded, isSignedIn, userId]);
  
  return children;
};

// Import your publishable key
const PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!PUBLISHABLE_KEY) {
  throw new Error("Missing Publishable Key");
}

ReactDOM.createRoot(document.getElementById("root")).render(
  <React.StrictMode>
    <ClerkProvider
      publishableKey={PUBLISHABLE_KEY}
      localization={esMX}
      afterSignOutUrl="/"
      appearance={{
        variables: {
          colorPrimary: '#d97706',
          colorTextOnPrimaryBackground: '#ffffff',
        },
        elements: {
          rootBox: "font-sans",
          card: "rounded-xl shadow-lg",
        }
      }}
      // Listen for the signup event to track new registrations
      signUpUrl={() => {
        if (typeof window !== 'undefined') {
          const timestamp = Date.now().toString();
          localStorage.setItem(`clerk_signup_time_${timestamp}`, timestamp);
        }
        return '/';
      }}
    >
      <AuthObserver>
        <App />
      </AuthObserver>
    </ClerkProvider>
  </React.StrictMode>
);
